/**
 * 公式提取工具 — 从文本中识别数学公式区域
 *
 * MVP 方案：基于启发式正则匹配常见公式模式。
 * 完整方案需 MathPix API 或 Nougat 模型（v2）。
 *
 * 对齐 features.md 1.16
 */

/**
 * 检测文本是否可能是公式（启发式）
 * 匹配模式：含数学符号、上下标、求和/积分、分式等
 */
export function isFormula(text: string): boolean {
  const formulaPatterns = [
    /[∑∫∏∂∇√∞≈≤≥≠±×÷]/,           // 数学符号
    /[α-ωΑ-Ω]/,                      // 希腊字母
    /\^[0-9{]/,                      // 上标
    /_[0-9{]/,                       // 下标
    /\\(frac|sqrt|sum|int|lim|alpha|beta|gamma|delta|theta|lambda|sigma|infty)/, // LaTeX 命令
    /=.*[0-9]+\s*[+\-*/]\s*[0-9]+/,  // 等式
    /[df]\/d[xty]/,                  // 微分
  ];

  return formulaPatterns.some((pattern) => pattern.test(text));
}

/**
 * 尝试将文本转换为 LaTeX
 * MVP: 简单的符号映射，不完整
 */
export function textToLatex(text: string): string {
  let latex = text;

  // 常见符号映射
  const replacements: [RegExp, string][] = [
    [/×/g, '\\times'],
    [/÷/g, '\\div'],
    [/±/g, '\\pm'],
    [/≤/g, '\\leq'],
    [/≥/g, '\\geq'],
    [/≠/g, '\\neq'],
    [/∞/g, '\\infty'],
    [/√/g, '\\sqrt'],
    [/∑/g, '\\sum'],
    [/∫/g, '\\int'],
    [/∂/g, '\\partial'],
    [/α/g, '\\alpha'],
    [/β/g, '\\beta'],
    [/γ/g, '\\gamma'],
    [/δ/g, '\\delta'],
    [/θ/g, '\\theta'],
    [/λ/g, '\\lambda'],
    [/σ/g, '\\sigma'],
    [/π/g, '\\pi'],
    [/→/g, '\\rightarrow'],
    [/⇒/g, '\\Rightarrow'],
  ];

  for (const [pattern, replacement] of replacements) {
    latex = latex.replace(pattern, replacement);
  }

  return latex;
}

/**
 * 从 block 文本中提取公式
 * 返回：如果是公式，返回 LaTeX；否则返回 null
 */
export function extractFormula(text: string): { latex: string; original: string } | null {
  const trimmed = text.trim();
  if (trimmed.length > 500 || trimmed.length < 3) return null;

  if (isFormula(trimmed)) {
    return {
      latex: textToLatex(trimmed),
      original: trimmed,
    };
  }

  return null;
}
